const finite = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const calculateNetworkQuality = (metrics = {}) => {
  const isOnline = metrics.isOnline !== false;
  const connectionState = String(metrics.connectionState || '').toLowerCase();
  if (!isOnline || ['failed', 'closed'].includes(connectionState)) {
    return { score: 0, label: 'Offline', tone: 'offline' };
  }
  if (connectionState === 'disconnected') {
    return { score: 15, label: 'Reconnecting', tone: 'checking' };
  }

  const bitrateKbps = finite(metrics.bitrateKbps);
  const videoBitrateKbps = finite(metrics.videoBitrateKbps);
  const packetLoss = finite(metrics.packetLoss);
  const rttMs = finite(metrics.rttMs);
  const jitterMs = finite(
    metrics.jitterMs != null
      ? metrics.jitterMs
      : finite(metrics.jitter) != null
        ? Number(metrics.jitter) * 1000
        : null
  );
  const hasMeasurement = [
    bitrateKbps,
    packetLoss,
    rttMs,
    jitterMs,
  ].some((value) => value != null);
  const sampledAt = finite(metrics.sampledAt);
  const sampleIsStale =
    sampledAt != null && Date.now() - sampledAt > 15000;
  if (
    !hasMeasurement ||
    sampleIsStale ||
    ['new', 'connecting', 'checking'].includes(connectionState)
  ) {
    return { score: 25, label: 'Checking', tone: 'checking' };
  }
  const hasVideo =
    videoBitrateKbps > 0 ||
    ['sending', 'receiving', 'connecting', 'stalled'].includes(
      String(
        metrics.media?.video?.status ||
        metrics.media?.screen?.status ||
          ''
      )
    );
  const mediaStatuses = Object.values(metrics.media || {})
    .map((entry) => String(entry?.status || '').toLowerCase())
    .filter(Boolean);
  if (
    mediaStatuses.length > 0 &&
    mediaStatuses.every((status) =>
      ['paused', 'off', 'disabled', 'unavailable'].includes(status)
    )
  ) {
    return { score: 25, label: 'Media off', tone: 'checking' };
  }
  const videoStalled =
    String(metrics.media?.video?.status || '') === 'stalled' ||
    String(metrics.media?.screen?.status || '') === 'stalled';
  const bandwidthLimited =
    String(metrics.qualityLimitationReason || '').toLowerCase() ===
      'bandwidth' ||
    metrics.media?.video?.bandwidthLimited === true ||
    metrics.media?.screen?.bandwidthLimited === true;

  let score = 100;
  if (bitrateKbps != null) {
    if (hasVideo) {
      // Observed encoder bitrate is not link capacity: a static picture can
      // legitimately use very few bits. Apply a strong penalty only when
      // playback has stalled or WebRTC says bandwidth is limiting quality.
      if (videoStalled) {
        if (bitrateKbps < 40) score -= 70;
        else if (bitrateKbps < 100) score -= 55;
        else if (bitrateKbps < 250) score -= 35;
        else score -= 20;
      } else if (bandwidthLimited) {
        if (bitrateKbps < 100) score -= 35;
        else if (bitrateKbps < 250) score -= 24;
        else if (bitrateKbps < 500) score -= 14;
        else if (bitrateKbps < 900) score -= 7;
      } else if (bitrateKbps < 100) {
        score -= 12;
      } else if (bitrateKbps < 250) {
        score -= 6;
      }
    } else {
      // Opus speech with DTX is intentionally very small; 12–40 kbps can be
      // an excellent audio-only connection.
      if (bitrateKbps <= 0) score -= 30;
      else if (bitrateKbps < 12) score -= 15;
    }
  }

  if (packetLoss != null) {
    if (packetLoss >= 15) score -= 38;
    else if (packetLoss >= 8) score -= 26;
    else if (packetLoss >= 3) score -= 14;
    else if (packetLoss >= 1) score -= 5;
  }
  if (rttMs != null) {
    if (rttMs >= 600) score -= 35;
    else if (rttMs >= 350) score -= 25;
    else if (rttMs >= 200) score -= 15;
    else if (rttMs >= 120) score -= 7;
  }
  if (jitterMs != null) {
    if (jitterMs >= 100) score -= 22;
    else if (jitterMs >= 50) score -= 13;
    else if (jitterMs >= 30) score -= 6;
  }
  if (videoStalled) score -= 35;

  score = Math.max(1, Math.min(100, Math.round(score)));
  if (score >= 85) return { score, label: 'Excellent', tone: 'excellent' };
  if (score >= 65) return { score, label: 'Good', tone: 'good' };
  if (score >= 40) return { score, label: 'Fair', tone: 'fair' };
  return { score, label: 'Poor', tone: 'poor' };
};

/**
 * Network health and delivered picture quality are deliberately separate.
 * A user can intentionally choose Very Low on a perfect connection, while a
 * weak camera can produce a low-resolution image without packet loss.
 */
export const getVideoDeliveryQuality = (metrics = {}) => {
  const screenStatus = String(
    metrics.media?.screen?.status || ''
  ).toLowerCase();
  const video =
    ['receiving', 'connecting', 'stalled'].includes(screenStatus)
      ? metrics.media?.screen
      : metrics.media?.video;
  const status = String(video?.status || '').toLowerCase();
  if (!video || ['paused', 'off', 'disabled', 'unavailable'].includes(status)) {
    return null;
  }
  if (status === 'stalled') {
    return { key: 'stalled', label: 'Video stalled' };
  }
  const height = finite(video.frameHeight);
  if (!(height > 0)) {
    return status === 'connecting'
      ? { key: 'checking', label: 'Video checking' }
      : null;
  }
  if (height >= 650) return { key: 'hd', label: 'Video HD' };
  if (height >= 480) return { key: 'high', label: 'Video High' };
  if (height >= 320) return { key: 'medium', label: 'Video Medium' };
  if (height >= 220) return { key: 'low', label: 'Video Low' };
  return { key: 'verylow', label: 'Video Very Low' };
};

export const formatNetworkBitrate = (bitrateKbps) => {
  const bitrate = finite(bitrateKbps);
  if (bitrate == null) return '';
  const safeBitrate = Math.max(0, bitrate);
  if (safeBitrate < 1000) return `${Math.round(safeBitrate)} kbps`;
  const mbps = safeBitrate / 1000;
  const digits = mbps >= 10 || Number.isInteger(mbps) ? 1 : 2;
  return `${Number(mbps.toFixed(digits))} Mbps`;
};

export const formatNetworkDetails = (metrics = {}) => {
  const parts = [];
  const bitrate = finite(metrics.bitrateKbps);
  const rtt = finite(metrics.rttMs);
  const loss = finite(metrics.packetLoss);
  const visibleVideo =
    ['receiving', 'connecting', 'stalled'].includes(
      String(metrics.media?.screen?.status || '').toLowerCase()
    )
      ? metrics.media.screen
      : metrics.media?.video;
  const frameWidth = finite(visibleVideo?.frameWidth);
  const frameHeight = finite(visibleVideo?.frameHeight);
  const framesPerSecond = finite(visibleVideo?.framesPerSecond);
  if (bitrate != null) parts.push(formatNetworkBitrate(bitrate));
  if (frameWidth > 0 && frameHeight > 0) {
    parts.push(
      `${Math.round(frameWidth)}×${Math.round(frameHeight)}`
    );
  }
  if (framesPerSecond > 0) {
    parts.push(`${Math.round(framesPerSecond)} fps`);
  }
  if (rtt != null) parts.push(`${Math.max(0, Math.round(rtt))} ms`);
  if (loss != null) parts.push(`${Math.max(0, loss).toFixed(1)}% loss`);
  return parts.join(' · ');
};
